'use client';
import { resetAdditionalData } from '@/redux/additionalDataSlice';
import { errorReason } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Button, FormHelperText } from '@mui/material';
import { submitStep } from '@repo/onb-api';
import { IconBadge, ResultScreen } from '@repo/ui';
import { deleteClientCookie, getClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export default function OnbError({ message, hideButtons }: { message?: string; hideButtons?: boolean }) {
  const router = useRouter();
  const [isReady, setIsReady] = useState(false);
  const { errorReason } = useSelector((state: RootState) => state.navigationdata);
  const isInternal = useSelector((state: RootState) => state.additionalData.internal);
  const instanceId = getClientCookie('instanceId');
  const instanceIdFromRedux = useSelector((state: RootState) => state.flowdata.id);
  const isCuotas = process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-cuotas';
  const smsInterval = useRef(0);
  const [smsTimeout, setSmsTimeout] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    if (instanceId && !instanceIdFromRedux) {
      router.push('/');
    } else {
      setIsReady(true);
    }
  }, [instanceId, instanceIdFromRedux, router]);

  useEffect(() => {
    smsInterval.current = window.setTimeout(() => {
      setSmsTimeout((t) => t - 1);
    }, 1000);
    if (smsTimeout <= 0) clearTimeout(smsInterval.current);
  }, [smsTimeout]);

  const returnAction = () => {
    if (isCuotas && !isInternal) {
      router.push('/back_to_store');
    } else {
      router.push('/');
    }
  };

  const reasonMessage: Record<errorReason, string> = {
    slowService: 'El servicio está un poco lento, reintentalo a la brevedad.',
    timeout: 'Tu tiempo de sesión se agotó, volvé a intentarlo.',
    accessKey: 'Continuá desde el link enviado a tu celular.',
  };

  if (!isReady) return null;

  return (
    <ResultScreen
      status={errorReason === 'accessKey' ? 'info' : 'error'}
      visual={errorReason === 'accessKey' ? <IconBadge icon="sms" tone="brand" /> : undefined}
      title={errorReason ? reasonMessage[errorReason] : message || '¡Ups! Algo salió mal'}
      actions={
        !hideButtons && (
          <>
            {errorReason === 'accessKey' ? (
              instanceId && (
                <>
                  <Button
                    disabled={smsTimeout > 0}
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setSmsTimeout(45);
                      submitStep({
                        instanceId,
                        step: {
                          name: 'REVIEW_AND_SEND_LINK',
                        },
                      });
                    }}
                  >
                    Reenviar mensaje
                  </Button>
                  <FormHelperText sx={{ alignSelf: 'center', py: 1, display: smsTimeout > 0 ? 'block' : 'none' }}>
                    Código enviado. Para reenviar otro, esperá 00:{`${smsTimeout}`.padStart(2, '0')}
                  </FormHelperText>
                </>
              )
            ) : (
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  router.push('/');
                  router.refresh();
                }}
              >
                Reintentar
              </Button>
            )}
            <Button
              sx={{ display: !isCuotas || !isInternal ? 'block' : 'none' }}
              fullWidth
              variant="inverted"
              onClick={() => {
                deleteClientCookie([
                  'instanceId',
                  'requestedAmount',
                  'integrationType',
                  'policyProduct',
                  'utmData',
                  'orderToken',
                  'accessKey',
                ]);
                dispatch(resetAdditionalData());
                returnAction();
                router.refresh();
              }}
            >
              {isCuotas && !isInternal ? 'Volver a la tienda' : 'Volver al inicio'}
            </Button>
          </>
        )
      }
    />
  );
}
